import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { CloudDownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type {
  AlfaPeoplePerson,
  DictionarySyncConfig,
  DictionarySyncPreview,
  DictionarySyncRun,
  DictionarySyncValueChange,
} from '@lens/shared';
import { alfaPeopleExportUrl, normalizeAlfaPeople, parseAlfaPeopleExport } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { applyDictionarySync, previewDictionarySync } from './api.ts';
import { fetchAlfaPeopleExports, pingAlfaPeopleExtension } from './alfaPeopleBridge.ts';

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  dictId: string;
  config: DictionarySyncConfig;
  /** applied — было ли применение: тогда страница перечитывает записи. */
  onClose: (applied: boolean) => void;
  /** После применения: перечитать историю запусков. */
  onDone: (run: DictionarySyncRun) => void;
}

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

function Changes({ changes, onlyTo }: { changes: DictionarySyncValueChange[]; onlyTo?: boolean }) {
  return (
    <Space direction="vertical" size={0}>
      {changes.map((c) => (
        <Text key={c.columnId} style={{ fontSize: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {c.columnName}:
          </Text>{' '}
          {onlyTo ? (
            c.toLabel
          ) : (
            <>
              <Text delete type="secondary" style={{ fontSize: 12 }}>
                {c.fromLabel}
              </Text>{' '}
              → {c.toLabel}
            </>
          )}
        </Text>
      ))}
    </Space>
  );
}

/**
 * «Обновить из Alfa People»: забрать выгрузку (расширением или файлом) →
 * сравнить со справочником → отметить, что применить → применить.
 */
export function AlfaPeopleSyncModal({ open, dictId, config, onClose, onDone }: Props): JSX.Element {
  const [step, setStep] = useState(0);
  const [extVersion, setExtVersion] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<AlfaPeoplePerson[]>([]);
  const [preview, setPreview] = useState<DictionarySyncPreview | null>(null);
  const [selCreate, setSelCreate] = useState<string[]>([]);
  const [selUpdate, setSelUpdate] = useState<string[]>([]);
  const [selDeactivate, setSelDeactivate] = useState<string[]>([]);
  const [result, setResult] = useState<DictionarySyncRun | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setProgress('');
    setPreview(null);
    setResult(null);
    setExtVersion(undefined);
    void pingAlfaPeopleExtension().then(setExtVersion);
  }, [open]);

  async function compare(raws: unknown[]) {
    setError(null);
    setBusy(true);
    try {
      const list = normalizeAlfaPeople(raws.flatMap((r) => parseAlfaPeopleExport(r)));
      if (list.length === 0) throw new Error('В выгрузке нет ни одного человека с логином');
      setProgress('Сравниваем со справочником…');
      const p = await previewDictionarySync(dictId, list);
      setPeople(list);
      setPreview(p);
      setSelCreate(p.create.map((c) => c.key));
      setSelUpdate(p.update.map((u) => u.entryId));
      setSelDeactivate([]);
      setStep(1);
    } catch (e) {
      setError(errText(e, 'Не удалось сравнить выгрузку'));
    } finally {
      setBusy(false);
      setProgress('');
    }
  }

  async function fromExtension() {
    setError(null);
    setBusy(true);
    setProgress('Забираем выгрузку…');
    try {
      const exports = await fetchAlfaPeopleExports(config.managers, setProgress);
      await compare(exports.map((x) => x.data));
    } catch (e) {
      setError(errText(e, 'Не удалось забрать выгрузку'));
      setBusy(false);
      setProgress('');
    }
  }

  async function fromFiles(files: File[]) {
    try {
      const raws = await Promise.all(files.map(async (f) => JSON.parse(await f.text()) as unknown));
      await compare(raws);
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? 'Файл — не JSON. Сохраните ответ портала как есть.'
          : errText(e, 'Не удалось прочитать файл'),
      );
    }
  }

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      const run = await applyDictionarySync(dictId, {
        people,
        create: selCreate,
        update: selUpdate,
        deactivate: selDeactivate,
      });
      setResult(run);
      setStep(2);
      onDone(run);
      message.success('Справочник обновлён');
    } catch (e) {
      setError(errText(e, 'Не удалось применить изменения'));
    } finally {
      setBusy(false);
    }
  }

  const total = selCreate.length + selUpdate.length + selDeactivate.length;
  const canDeactivate = !!config.activeColumnId;

  // Без useMemo: кнопка «Применить» должна видеть текущие отметки.
  const footer =
    step === 1
      ? [
          <Button key="back" onClick={() => setStep(0)} disabled={busy}>
            Назад
          </Button>,
          <Button
            key="apply"
            type="primary"
            loading={busy}
            disabled={total === 0}
            onClick={() => void apply()}
            data-testid="ap-sync-apply"
          >
            Применить выбранное ({total})
          </Button>,
        ]
      : [
          <Button key="close" onClick={() => onClose(result !== null)}>
            {step === 2 ? 'Готово' : 'Отмена'}
          </Button>,
        ];

  return (
    <Modal
      open={open}
      title="Обновить из Alfa People"
      onCancel={() => onClose(result !== null)}
      footer={footer}
      width={920}
      destroyOnHidden
      data-testid="ap-sync-modal"
    >
      <Steps
        size="small"
        current={step}
        items={[{ title: 'Выгрузка' }, { title: 'Сравнение' }, { title: 'Готово' }]}
        style={{ marginBottom: 16 }}
      />
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 12 }}
          data-testid="ap-sync-error"
        />
      )}

      {step === 0 && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            Руководители:{' '}
            {config.managers.map((m) => (
              <Tag key={m}>{m}</Tag>
            ))}
          </Paragraph>
          {extVersion === undefined ? (
            <Spin size="small" />
          ) : extVersion ? (
            <Space direction="vertical">
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                loading={busy}
                onClick={() => void fromExtension()}
                data-testid="ap-sync-fetch"
              >
                Забрать из Alfa People
              </Button>
              <Text type="secondary">
                Расширение {extVersion}. Если портал попросит войти — войдите во вкладке, которая
                откроется, выгрузка продолжится сама.
              </Text>
            </Space>
          ) : (
            <Alert
              type="info"
              showIcon
              data-testid="ap-sync-no-extension"
              message="Расширение «Lens · Alfa People» не найдено"
              description={
                <>
                  Установите его из папки <Text code>extensions/alfa-people</Text>{' '}
                  (chrome://extensions → «Режим разработчика» → «Загрузить распакованное») и
                  обновите страницу. Или откройте выгрузку в браузере, сохраните ответ файлом и
                  загрузите ниже:{' '}
                  {config.managers.map((m) => (
                    <a
                      key={m}
                      href={alfaPeopleExportUrl(m)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginRight: 8 }}
                    >
                      {m}
                    </a>
                  ))}
                </>
              }
            />
          )}
          <Upload
            accept=".json,application/json"
            multiple
            showUploadList={false}
            beforeUpload={(_f, list) => {
              if (_f === list[list.length - 1]) void fromFiles(list as unknown as File[]);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} disabled={busy} data-testid="ap-sync-upload">
              Загрузить файл выгрузки
            </Button>
          </Upload>
          {progress && <Text type="secondary">{progress}</Text>}
        </Space>
      )}

      {step === 1 && preview && (
        <Space
          direction="vertical"
          size="middle"
          style={{ width: '100%' }}
          data-testid="ap-sync-preview"
        >
          <Space wrap>
            <Tag>В выгрузке: {preview.sourceCount}</Tag>
            <Tag color="green">Новых: {preview.create.length}</Tag>
            <Tag color="blue">Изменилось: {preview.update.length}</Tag>
            <Tag color="orange">Нет в портале: {preview.missing.length}</Tag>
            <Tag>Без изменений: {preview.unchanged}</Tag>
          </Space>
          {preview.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Не всё удалось сопоставить"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.warnings.slice(0, 20).map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                  {preview.warnings.length > 20 && <li>…и ещё {preview.warnings.length - 20}</li>}
                </ul>
              }
            />
          )}

          {preview.create.length > 0 && (
            <div>
              <Text strong>Новые — завести в справочник</Text>
              <Table
                size="small"
                rowKey="key"
                pagination={false}
                scroll={{ y: 260 }}
                dataSource={preview.create}
                rowSelection={{
                  selectedRowKeys: selCreate,
                  onChange: (k) => setSelCreate(k as string[]),
                }}
                columns={[
                  { title: 'Кто', dataIndex: 'label', width: 240 },
                  {
                    title: 'Что запишем',
                    render: (_, r) => <Changes changes={r.changes} onlyTo />,
                  },
                ]}
                data-testid="ap-sync-create-table"
              />
            </div>
          )}

          {preview.update.length > 0 && (
            <div>
              <Text strong>Изменилось в портале</Text>
              <Table
                size="small"
                rowKey="entryId"
                pagination={false}
                scroll={{ y: 260 }}
                dataSource={preview.update}
                rowSelection={{
                  selectedRowKeys: selUpdate,
                  onChange: (k) => setSelUpdate(k as string[]),
                }}
                columns={[
                  { title: 'Кто', dataIndex: 'label', width: 240 },
                  { title: 'Было → стало', render: (_, r) => <Changes changes={r.changes} /> },
                ]}
                data-testid="ap-sync-update-table"
              />
            </div>
          )}

          {preview.missing.length > 0 && (
            <div>
              <Text strong>Есть в справочнике, нет в портале</Text>
              <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                {canDeactivate
                  ? 'Отмеченным снимем «активен». Записи не удаляются: на них ссылаются карточки.'
                  : 'Колонка «активен» не настроена — только для сведения.'}
              </Paragraph>
              <Table
                size="small"
                rowKey="entryId"
                pagination={false}
                scroll={{ y: 200 }}
                dataSource={preview.missing}
                rowSelection={
                  canDeactivate
                    ? {
                        selectedRowKeys: selDeactivate,
                        onChange: (k) => setSelDeactivate(k as string[]),
                      }
                    : undefined
                }
                columns={[
                  { title: 'Кто', dataIndex: 'label' },
                  { title: 'Логин', dataIndex: 'key', width: 200 },
                ]}
                data-testid="ap-sync-missing-table"
              />
            </div>
          )}

          {preview.create.length + preview.update.length + preview.missing.length === 0 && (
            <Alert type="success" showIcon message="Справочник совпадает с порталом" />
          )}
        </Space>
      )}

      {step === 2 && result && (
        <Alert
          type="success"
          showIcon
          data-testid="ap-sync-result"
          message="Готово"
          description={`Заведено: ${result.created}, обновлено: ${result.updated}, снято с «активен»: ${result.deactivated}.`}
        />
      )}
    </Modal>
  );
}
